"use client";

import { c as _c } from "react/compiler-runtime";
import { useState } from "react";
import { useForm } from "react-hook-form";
import Button from "@/components/ui/button";
import CollapsibleText from "@/components/ui/collapsible-text";
import { DatePickerWithYearDropdown } from "@/components/ui/date-picker";
import { Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import Input from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useUser } from "@/contexts/user-provider";
import { useClinicClock } from "@/hooks/use-clinic-clock";
import { dayMarker } from "@/lib/clinic-clock";
import { MeasurementCreate, MeasurementSummary } from "@/types/measurement";
import { useClinicDayField } from "./use-clinic-day-field";
import { useAddMeasurementMutation } from "@/api/provider/user/patient/patient-mutation";
interface AddBpMeasurementProps {
  patientId: string;
  onSubmit?: (data: MeasurementSummary) => void;
  children: React.ReactNode;
  selectedNoteId?: string;
  selectedEventId?: string;
}
type FormValues = {
  systolicBp: number;
  diastolicBp: number;
  pulse?: number;
};
export default function AddBpMeasurement(t0) {
  const $ = _c(73);
  const {
    patientId,
    onSubmit,
    children,
    selectedNoteId,
    selectedEventId
  } = t0;
  const [open, setOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = new Date();
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [measurementDate, setMeasurementDate] = useState(t1);
  const addMeasurementMutation = useAddMeasurementMutation();
  const {
    currentUser
  } = useUser();
  const clock = useClinicClock();
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = moment => setMeasurementDate(moment ?? null);
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const measurementDay = useClinicDayField(measurementDate, t2);
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      defaultValues: {
        systolicBp: undefined,
        diastolicBp: undefined,
        pulse: undefined
      },
      mode: "onSubmit"
    };
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const {
    register,
    handleSubmit,
    reset,
    formState: t4
  } = useForm(t3);
  const {
    errors
  } = t4;
  let t5;
  if ($[3] !== addMeasurementMutation || $[4] !== currentUser?.id || $[5] !== measurementDate || $[6] !== onSubmit || $[7] !== patientId || $[8] !== reset || $[9] !== selectedEventId || $[10] !== selectedNoteId) {
    t5 = async data => {
      if (!currentUser?.id) {
        return;
      }
      setIsSubmitting(true);
      const formData = {
        patientId,
        systolicBp: data.systolicBp,
        diastolicBp: data.diastolicBp,
        pulse: data.pulse,
        noteId: selectedNoteId,
        eventId: selectedEventId,
        measurementDate: measurementDate?.toISOString() ?? new Date().toISOString()
      };
      const result = await addMeasurementMutation.mutateAsync(formData);
      if (result) {
        onSubmit?.(result);
        reset();
        setMeasurementDate(new Date());
        setOpen(false);
      }
      setIsSubmitting(false);
    };
    $[3] = addMeasurementMutation;
    $[4] = currentUser?.id;
    $[5] = measurementDate;
    $[6] = onSubmit;
    $[7] = patientId;
    $[8] = reset;
    $[9] = selectedEventId;
    $[10] = selectedNoteId;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const handleFormSubmit = t5;
  let t6;
  if ($[12] !== reset) {
    t6 = () => {
      reset();
      setMeasurementDate(new Date());
      setOpen(false);
    };
    $[12] = reset;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  const closeDialog = t6;
  let t7;
  if ($[14] !== children) {
    t7 = <DialogTrigger asChild={true}>{children}</DialogTrigger>;
    $[14] = children;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <DialogHeader><DialogTitle>Add Blood Pressure Measurement</DialogTitle><DialogDescription>The AI will add measurements automatically if they're mentioned in the note, but you can also add them manually here.</DialogDescription></DialogHeader>;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== handleFormSubmit || $[18] !== handleSubmit) {
    t9 = handleSubmit(handleFormSubmit);
    $[17] = handleFormSubmit;
    $[18] = handleSubmit;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <div className="flex items-center gap-1"><Label htmlFor="systolicBp">Systolic BP</Label><span className="text-caption text-muted-foreground">(mmHg)</span></div>;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  const t11 = !!errors.systolicBp;
  const t12 = !!errors.systolicBp;
  const t13 = errors.systolicBp?.message;
  let t14;
  if ($[21] !== register) {
    t14 = register("systolicBp", {
      required: "Systolic BP is required",
      min: {
        value: 70,
        message: "Value seems too low"
      },
      max: {
        value: 250,
        message: "Value seems too high"
      },
      valueAsNumber: true
    });
    $[21] = register;
    $[22] = t14;
  } else {
    t14 = $[22];
  }
  let t15;
  if ($[23] !== t11 || $[24] !== t12 || $[25] !== t13 || $[26] !== t14) {
    t15 = <div className="space-y-1">{t10}<Input id="systolicBp" type="number" className="w-full" placeholder="Enter systolic pressure" aria-invalid={t11} hasError={t12} errorMessage={t13} {...t14} /></div>;
    $[23] = t11;
    $[24] = t12;
    $[25] = t13;
    $[26] = t14;
    $[27] = t15;
  } else {
    t15 = $[27];
  }
  let t16;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <div className="flex items-center gap-1"><Label htmlFor="diastolicBp">Diastolic BP</Label><span className="text-caption text-muted-foreground">(mmHg)</span></div>;
    $[28] = t16;
  } else {
    t16 = $[28];
  }
  const t17 = !!errors.diastolicBp;
  const t18 = !!errors.diastolicBp;
  const t19 = errors.diastolicBp?.message;
  let t20;
  if ($[29] !== register) {
    t20 = register("diastolicBp", {
      required: "Diastolic BP is required",
      min: {
        value: 40,
        message: "Value seems too low"
      },
      max: {
        value: 150,
        message: "Value seems too high"
      },
      valueAsNumber: true
    });
    $[29] = register;
    $[30] = t20;
  } else {
    t20 = $[30];
  }
  let t21;
  if ($[31] !== t17 || $[32] !== t18 || $[33] !== t19 || $[34] !== t20) {
    t21 = <div className="space-y-1">{t16}<Input id="diastolicBp" type="number" className="w-full" placeholder="Enter diastolic pressure" aria-invalid={t17} hasError={t18} errorMessage={t19} {...t20} /></div>;
    $[31] = t17;
    $[32] = t18;
    $[33] = t19;
    $[34] = t20;
    $[35] = t21;
  } else {
    t21 = $[35];
  }
  let t22;
  if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = <div className="flex items-center gap-1"><Label htmlFor="pulse">Pulse</Label><span className="text-caption text-muted-foreground">(bpm)</span></div>;
    $[36] = t22;
  } else {
    t22 = $[36];
  }
  const t23 = !!errors.pulse;
  const t24 = !!errors.pulse;
  const t25 = errors.pulse?.message;
  let t26;
  if ($[37] !== register) {
    t26 = register("pulse", {
      min: {
        value: 30,
        message: "Value seems too low"
      },
      max: {
        value: 220,
        message: "Value seems too high"
      },
      valueAsNumber: true
    });
    $[37] = register;
    $[38] = t26;
  } else {
    t26 = $[38];
  }
  let t27;
  if ($[39] !== t23 || $[40] !== t24 || $[41] !== t25 || $[42] !== t26) {
    t27 = <div className="space-y-1">{t22}<Input id="pulse" type="number" className="w-full" placeholder="Enter pulse in beats per minute" aria-invalid={t23} hasError={t24} errorMessage={t25} {...t26} /></div>;
    $[39] = t23;
    $[40] = t24;
    $[41] = t25;
    $[42] = t26;
    $[43] = t27;
  } else {
    t27 = $[43];
  }
  let t28;
  if ($[44] !== t15 || $[45] !== t21 || $[46] !== t27 || $[47] !== t9) {
    t28 = <form id="add-bp-measurement-form" onSubmit={t9} className="space-y-4">{t15}{t21}{t27}</form>;
    $[44] = t15;
    $[45] = t21;
    $[46] = t27;
    $[47] = t9;
    $[48] = t28;
  } else {
    t28 = $[48];
  }
  let t29;
  if ($[49] === Symbol.for("react.memo_cache_sentinel")) {
    t29 = <Label htmlFor="measurement-date">Measurement Date</Label>;
    $[49] = t29;
  } else {
    t29 = $[49];
  }
  const t30 = measurementDay.value;
  const t31 = measurementDay.onChange;
  let t32;
  if ($[50] !== clock) {
    t32 = dayMarker(clock.today());
    $[50] = clock;
    $[51] = t32;
  } else {
    t32 = $[51];
  }
  let t33;
  if ($[52] !== measurementDay.onChange || $[53] !== measurementDay.value || $[54] !== t32) {
    t33 = <CollapsibleText text="Set measurement date" className="mt-4"><div className="space-y-2">{t29}<DatePickerWithYearDropdown value={t30} onChange={t31} placeholder="Select measurement date" maxDate={t32} /></div></CollapsibleText>;
    $[52] = measurementDay.onChange;
    $[53] = measurementDay.value;
    $[54] = t32;
    $[55] = t33;
  } else {
    t33 = $[55];
  }
  let t34;
  if ($[56] !== t28 || $[57] !== t33) {
    t34 = <DialogBody>{t28}{t33}</DialogBody>;
    $[56] = t28;
    $[57] = t33;
    $[58] = t34;
  } else {
    t34 = $[58];
  }
  let t35;
  if ($[59] !== closeDialog) {
    t35 = <Button variant="secondary" type="button" onClick={closeDialog}>Cancel</Button>;
    $[59] = closeDialog;
    $[60] = t35;
  } else {
    t35 = $[60];
  }
  let t36;
  if ($[61] !== isSubmitting) {
    t36 = <Button variant="primary" type="submit" disabled={isSubmitting} isLoading={isSubmitting} loadingText="Saving..." form="add-bp-measurement-form">Save Measurement</Button>;
    $[61] = isSubmitting;
    $[62] = t36;
  } else {
    t36 = $[62];
  }
  let t37;
  if ($[63] !== t35 || $[64] !== t36) {
    t37 = <DialogFooter>{t35}{t36}</DialogFooter>;
    $[63] = t35;
    $[64] = t36;
    $[65] = t37;
  } else {
    t37 = $[65];
  }
  let t38;
  if ($[66] !== t34 || $[67] !== t37) {
    t38 = <DialogContent>{t8}{t34}{t37}</DialogContent>;
    $[66] = t34;
    $[67] = t37;
    $[68] = t38;
  } else {
    t38 = $[68];
  }
  let t39;
  if ($[69] !== open || $[70] !== t38 || $[71] !== t7) {
    t39 = <Dialog open={open} onOpenChange={setOpen}>{t7}{t38}</Dialog>;
    $[69] = open;
    $[70] = t38;
    $[71] = t7;
    $[72] = t39;
  } else {
    t39 = $[72];
  }
  return t39;
}